import React from "react";
import { getGDriveFiles, getGDriveFile } from "../../services/api/http";
import SiteLayout from "../../components/layout/SiteLayout";
import PageIntro from "../../components/ui/PageIntro";
import "./ReligiousPhotosPage.css";

const inferMimeType = (fileName) => {
  const normalized = String(fileName || "").toLowerCase();

  if (normalized.endsWith(".png")) return "image/png";
  if (normalized.endsWith(".jpg") || normalized.endsWith(".jpeg")) {
    return "image/jpeg";
  }
  if (normalized.endsWith(".webp")) return "image/webp";
  if (normalized.endsWith(".gif")) return "image/gif";
  if (normalized.endsWith(".heic")) return "image/heic";
  if (normalized.endsWith(".heif")) return "image/heif";

  return "application/octet-stream";
};

const isHeicFile = (fileName) => {
  const normalized = String(fileName || "").toLowerCase();
  return normalized.endsWith(".heic") || normalized.endsWith(".heif");
};

class LazyProtectedPhoto extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      shouldLoad: false,
      imageUrl: null,
      loading: false,
      failed: false
    };
    this.containerRef = React.createRef();
    this.observer = null;
    this.objectUrl = null;
  }

  componentDidMount() {
    if (typeof IntersectionObserver !== "function") {
      this.setState({ shouldLoad: true }, this.loadImageIfNeeded);
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.setState({ shouldLoad: true }, this.loadImageIfNeeded);
            if (this.observer) {
              this.observer.disconnect();
              this.observer = null;
            }
          }
        });
      },
      {
        rootMargin: "250px 0px",
        threshold: 0.01
      }
    );

    if (this.containerRef.current) {
      this.observer.observe(this.containerRef.current);
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.objectUrl) {
      window.URL.revokeObjectURL(this.objectUrl);
    }
  }

  loadImageIfNeeded = async () => {
    const { shouldLoad, imageUrl, loading } = this.state;
    const { photo } = this.props;

    if (!shouldLoad || imageUrl || loading) {
      return;
    }

    try {
      this.setState({ loading: true, failed: false });
      const response = await getGDriveFile(photo.id);
      const mimeType = inferMimeType(photo.name);
      const blob = new Blob([response.data], { type: mimeType });
      this.objectUrl = window.URL.createObjectURL(blob);

      this.setState({
        imageUrl: this.objectUrl,
        loading: false
      });
    } catch (error) {
      console.error(error);
      this.setState({
        loading: false,
        failed: true
      });
    }
  };

  handleImageError = () => {
    this.setState({
      loading: false,
      failed: true
    });
  };

  handleImageLoad = () => {
    this.setState({ loading: false });
  };

  renderStatus() {
    const { photo } = this.props;
    const { shouldLoad, loading, failed } = this.state;

    if (!shouldLoad) {
      return <div className="photo-placeholder">Loads as I scroll</div>;
    }

    if (loading) {
      return (
        <div className="photo-placeholder">
          <div className="spinner-dot" />
          <div className="spinner-dot" />
          <div className="spinner-dot" />
        </div>
      );
    }

    if (failed) {
      return (
        <div className="photo-placeholder photo-placeholder-error">
          Unable to load {photo.name}
        </div>
      );
    }

    return null;
  }

  render() {
    const { photo, onOpen, tileStyle } = this.props;
    const { imageUrl, loading } = this.state;

    return (
      <div
        className="photo-card"
        ref={this.containerRef}
        style={{
          "--photo-x": tileStyle?.x || "0%",
          "--photo-y": tileStyle?.y || "0px",
          "--photo-w": tileStyle?.w || "30%",
          "--photo-h": tileStyle?.h || "220px",
          "--photo-rotate": tileStyle?.rotate || "0deg",
          "--photo-z": tileStyle?.z || 1,
          "--photo-band": tileStyle?.band || 0,
          "--photo-ratio": tileStyle?.ratio || "4 / 3"
        }}
      >
        <div className="photo-frame">
          {imageUrl ? (
            <button
              type="button"
              className="photo-button"
              onClick={() => onOpen({ name: photo.name, src: imageUrl })}
              aria-label={`Open photo ${photo.name}`}
            >
              <img
                alt={photo.name}
                src={imageUrl}
                onLoad={this.handleImageLoad}
                onError={this.handleImageError}
                className={loading ? "is-loading" : ""}
              />
            </button>
          ) : null}
          {this.renderStatus()}
        </div>
      </div>
    );
  }
}

export default class ReligiousPhotosPage extends React.Component {
  constructor() {
    super();
    this.state = {
      drivePhotos: [],
      activePhoto: null
    };
  }

  componentDidMount() {
    getGDriveFiles()
      .then((resp) => {
        this.setState({
          drivePhotos: resp.data || []
        });
      })
      .catch((err) => {
        console.error(err);
      });
  }

  closePhoto = () => {
    this.setState({ activePhoto: null });
  };

  render() {
    const { drivePhotos, activePhoto } = this.state;
    const visiblePhotos = drivePhotos.filter((photo) => !isHeicFile(photo.name));
    const photoTiles = [
      { x: "2%", y: "24px", w: "26%", h: "170px", rotate: "-9deg", z: 6, ratio: "1 / 1" },
      { x: "18%", y: "6px", w: "34%", h: "240px", rotate: "5deg", z: 10, ratio: "4 / 5" },
      { x: "48%", y: "28px", w: "24%", h: "160px", rotate: "-4deg", z: 5, ratio: "1 / 1" },
      { x: "68%", y: "0px", w: "22%", h: "190px", rotate: "8deg", z: 8, ratio: "5 / 4" },
      { x: "9%", y: "206px", w: "20%", h: "142px", rotate: "7deg", z: 4, ratio: "3 / 4" },
      { x: "33%", y: "252px", w: "28%", h: "176px", rotate: "-11deg", z: 9, ratio: "16 / 10" },
      { x: "61%", y: "198px", w: "30%", h: "210px", rotate: "3deg", z: 3, ratio: "4 / 3" },
      { x: "76%", y: "258px", w: "18%", h: "136px", rotate: "-7deg", z: 7, ratio: "1 / 1" }
    ];
    const tileBandHeight = 438;
    const collageBands = Math.max(1, Math.ceil(visiblePhotos.length / photoTiles.length));

    return (
      <SiteLayout>
        <main className="page-shell photos-page home-v3-shell">
          <PageIntro
            eyebrow="Spirituality / Photos"
            title="Ceremony photos, kept as an archive."
            description="A visual record with lazy loading and a fullscreen viewer that stays out of the way."
          />

          <section className="section">
            <div className="photo-section-header">
              <div className="photo-section-copy-wrap">
                <span className="home-v3-kicker">Gallery</span>
                <h2>Watch-style collage.</h2>
                <p>
                  Open any image to see the full version. The layout is packed
                  like a collage so the gallery feels dense and immediate.
                </p>
              </div>
            </div>
            <div
              className="photo-collage"
              style={{
                "--collage-height": `${collageBands * tileBandHeight + 80}px`
              }}
            >
              {visiblePhotos.map((photo, index) => (
                <LazyProtectedPhoto
                  key={photo.id}
                  photo={photo}
                  tileStyle={{
                    ...photoTiles[index % photoTiles.length],
                    band: Math.floor(index / photoTiles.length)
                  }}
                  onOpen={(selectedPhoto) =>
                    this.setState({ activePhoto: selectedPhoto })
                  }
                />
              ))}
            </div>
          </section>
        </main>
        {activePhoto ? (
          <div
            className="photo-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label={activePhoto.name}
            onClick={this.closePhoto}
          >
            <button
              type="button"
              className="photo-lightbox-close"
              onClick={this.closePhoto}
              aria-label="Close photo viewer"
            >
              Close
            </button>
            <img
              className="photo-lightbox-image"
              src={activePhoto.src}
              alt={activePhoto.name}
              onClick={(event) => event.stopPropagation()}
            />
          </div>
        ) : null}
      </SiteLayout>
    );
  }
}
